import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { adminApi, REPORT_STATUS_LABEL, REPORT_STATUS_TONE, FIT_VERDICT_LABEL } from '@/lib/adminApi';
import { formatApiError, api } from '@/lib/api';
import { ReportDeleteDialog } from '@/components/admin/AdminDeleteDialogs';
import { useApp } from '@/context/AppContext';
import { toast } from 'sonner';
import { ArrowRight, Search, Loader2, FileText, RotateCcw, Trash2 } from 'lucide-react';
import { useSort } from '@/lib/useSort';
import { SortableHeader } from '@/components/shared/SortableHeader';

const STATUSES = ['all', 'processing', 'generation_failed', 'draft_ai_generated', 'under_review', 'edited_by_hm', 'pending_admin_review', 'approved', 'released_to_client', 'rejected', 'archived', 'discarded'];

const StatusPill = ({ status, generation }) => {
  const { t } = useTranslation();
  const tone = REPORT_STATUS_TONE[status] || REPORT_STATUS_TONE.draft_ai_generated;
  // Async Report Pipeline P2 — distinguish queued vs processing at a glance.
  const isQueued = status === 'processing' && generation?.state === 'queued';
  const label = isQueued
    ? t('reports.status.queued', { defaultValue: 'Generating report…' })
    : t(`reports.status.${status}`, { defaultValue: REPORT_STATUS_LABEL[status] || status });
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${tone.bg} ${tone.text} ${tone.border} border`}>
      {status === 'processing'
        ? <Loader2 className="h-3 w-3 animate-spin" />
        : <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} />}
      {label}
    </span>
  );
};

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return iso; }
};

export default function AdminReports() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useApp();
  const isAdvisor = user?.role === 'hm_advisor';
  const [searchParams, setSearchParams] = useSearchParams();
  const periodMonth = searchParams.get('period') === 'month';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  // Iter 41.10 — RBAC hardening: hm_advisor is ALWAYS book-scoped. The
  // "View all" toggle has been removed. Backend forcibly ignores ?book=all.
  const showAll = false;
  const [refreshTick, setRefreshTick] = useState(0);
  const [retryingId, setRetryingId] = useState(null);
  const [deleteFor, setDeleteFor] = useState(null);
  const reload = () => setRefreshTick((x) => x + 1);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const opts = {};
    adminApi.listReports(statusFilter === 'all' ? null : statusFilter, opts)
      .then((data) => { if (alive) setItems(data); })
      .catch((e) => toast.error(t('pages.reports.loadFailed', { defaultValue: 'Failed to load reports' }), { description: formatApiError(e) }))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [statusFilter, t, refreshTick]);

  // Async Report Pipeline P2 — while any report is generating, silently
  // refresh the list every 10s so statuses stay truthful without reloads.
  useEffect(() => {
    if (!items.some((r) => r.status === 'processing')) return undefined;
    const h = setInterval(() => setRefreshTick((n) => n + 1), 10000);
    return () => clearInterval(h);
  }, [items]);

  const retryRow = async (e, r) => {
    e.stopPropagation();
    setRetryingId(r.id);
    try {
      await adminApi.retryGeneration(r.id);
      toast.success(t('reports.generation.retryQueued', { defaultValue: 'Report generation restarted' }));
      setRefreshTick((n) => n + 1);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRetryingId(null);
    }
  };

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    let list = items;
    if (periodMonth) {
      const now = new Date();
      const y = now.getFullYear();
      const m = now.getMonth();
      list = list.filter((r) => {
        if (!r.created_at) return false;
        const d = new Date(r.created_at);
        return d.getFullYear() === y && d.getMonth() === m;
      });
    }
    if (!s) return list;
    return list.filter((r) => {
      const c = (r.candidate?.name || '').toLowerCase();
      const co = (r.company?.name || '').toLowerCase();
      return c.includes(s) || co.includes(s);
    });
  }, [items, search, periodMonth]);

  // BUG-platform — column sort
  const sort = useSort();
  const sortedRows = sort.apply(filtered, {
    evaluatee: (r) => (r.candidate?.name || '').toLowerCase(),
    company:   (r) => (r.company?.name || '').toLowerCase(),
    score:     (r) => r.scores?.overall_score ?? -1,
    fit:       (r) => r.fit?.fit_score ?? -1,
    status:    (r) => r.status || '',
    submitted: (r) => r.created_at ? new Date(r.created_at).getTime() : 0,
  });

  const counts = useMemo(() => {
    const c = {};
    items.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [items]);

  return (
    <>
      <Topbar title={t('pages.reports.title')} subtitle={t('pages.reports.subtitle')} />
      <div className="p-6 lg:p-8 space-y-6" data-testid="admin-reports-page">
        {/* status counters */}
        <div className="grid grid-cols-2 md:grid-cols-4 2xl:grid-cols-6 gap-3">
          {['draft_ai_generated', 'under_review', 'edited_by_hm', 'approved', 'released_to_client', 'rejected'].map((s) => (
            <Card key={s} className="p-4 shadow-card cursor-pointer hover:shadow-elegant transition-smooth"
              onClick={() => setStatusFilter(s)}
              data-testid={`status-counter-${s}`}>
              <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                {t(`reports.status.${s}`, { defaultValue: REPORT_STATUS_LABEL[s] })}
              </p>
              <p className="text-2xl font-display font-bold mt-1 tabular-nums">{counts[s] || 0}</p>
            </Card>
          ))}
        </div>

        {/* filters */}
        <Card className="p-4 shadow-card">
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                data-testid="reports-search"
                placeholder={t('pages.reports.searchPh')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="reports-status-filter" className="w-full sm:w-56 h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s === 'all' ? t('pages.reports.allStatuses') : t(`reports.status.${s}`, { defaultValue: REPORT_STATUS_LABEL[s] })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </Card>

        {/* table */}
        <Card className="shadow-card overflow-hidden">
          {loading ? (
            <div className="p-12 flex items-center justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('pages.reports.loading')}
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">{t('pages.reports.noResults')}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead><SortableHeader sort={sort} k="evaluatee" testIdPrefix="reports">{t('pages.reports.evaluatee')}</SortableHeader></TableHead>
                  <TableHead><SortableHeader sort={sort} k="company" testIdPrefix="reports">{t('pages.reports.company')}</SortableHeader></TableHead>
                  <TableHead><SortableHeader sort={sort} k="score" testIdPrefix="reports">{t('pages.reports.score')}</SortableHeader></TableHead>
                  <TableHead><SortableHeader sort={sort} k="fit" testIdPrefix="reports">{t('pages.reports.fit')}</SortableHeader></TableHead>
                  <TableHead><SortableHeader sort={sort} k="status" testIdPrefix="reports">{t('pages.reports.status')}</SortableHeader></TableHead>
                  <TableHead><SortableHeader sort={sort} k="submitted" testIdPrefix="reports">{t('pages.reports.submitted')}</SortableHeader></TableHead>
                  <TableHead className="text-right">{t('pages.reports.action')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedRows.map((r) => (
                  <TableRow
                    key={r.id}
                    data-testid={`report-row-${r.id}`}
                    className="cursor-pointer hover:bg-muted/40"
                    onClick={() => navigate(`/admin/reports/${r.id}`)}
                  >
                    <TableCell className="font-medium">{r.candidate?.name || '—'}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">{r.company?.name || '—'}</TableCell>
                    <TableCell className="tabular-nums">{r.report_mode === 'praxora_p4' ? 'P4' : (r.report_mode === 'dimensional_profile' ? 'Perfil' : (r.scores?.overall_score ?? '—'))}</TableCell>
                    <TableCell>
                      {r.fit ? (
                        <span className="text-xs">
                          <span className="font-semibold tabular-nums">{r.fit.fit_score}</span>{' '}
                          <Badge variant="outline" className="ml-1 text-xs">
                            {t(`reports.verdict.${r.fit.verdict}`, { defaultValue: FIT_VERDICT_LABEL[r.fit.verdict] })}
                          </Badge>
                        </span>
                      ) : <span className="text-xs text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell><StatusPill status={r.status} generation={r.generation} /></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{fmt(r.created_at)}</TableCell>
                    <TableCell className="text-right">
                      {r.status === 'generation_failed' && r.generation?.can_retry ? (
                        <Button
                          size="sm" variant="outline" className="gap-1.5 text-destructive border-destructive/30 hover:bg-destructive/10"
                          disabled={retryingId === r.id}
                          onClick={(e) => retryRow(e, r)}
                          data-testid={`report-retry-${r.id}`}
                        >
                          {retryingId === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                          {t('reports.generation.retryBtn', { defaultValue: 'Retry' })}
                        </Button>
                      ) : (
                        <span className="inline-flex items-center gap-1">
                          <Button size="sm" variant="ghost" className="gap-1.5">{t('pages.reports.open')} <ArrowRight className="h-3.5 w-3.5" /></Button>
                          {user?.role === 'hm_admin' && (
                            r.status === 'discarded' ? (
                              <Button size="sm" variant="ghost" className="text-emerald-700"
                                      onClick={(e) => { e.stopPropagation(); api.post(`/admin/reports/${r.id}/undiscard`).then(() => { toast.success(t('reports.restored', { defaultValue: 'Informe restaurado' })); reload(); }).catch((err) => toast.error(formatApiError(err))); }}
                                      data-testid={`report-restore-${r.id}`}
                                      title={t('reports.restore', { defaultValue: 'Restaurar' })}>
                                <RotateCcw className="h-3.5 w-3.5" />
                              </Button>
                            ) : (
                              <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700"
                                      onClick={(e) => { e.stopPropagation(); setDeleteFor(r); }}
                                      data-testid={`report-delete-${r.id}`}
                                      title={t('reports.delete', { defaultValue: 'Eliminar' })}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            )
                          )}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>
      <ReportDeleteDialog report={deleteFor} open={!!deleteFor}
                          onOpenChange={(v) => !v && setDeleteFor(null)} onDone={reload} />
    </>
  );
}
